import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { notificationsAPI } from './api';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Get token from localStorage
  const getToken = () => localStorage.getItem('token');

  // Get WebSocket URL — always use same-origin (window.location)
  // This matches the same-origin API strategy and avoids cross-origin WebSocket issues with CDNs.
  // Path is /api/ws/* so it gets forwarded to the FastAPI backend by the production proxy.
  const getWsUrl = () => {
    const token = getToken();
    if (!token) return null;
    // Connect to the API server (e.g. api.bridgewayglobalenglish.com), not the
    // website's own host: the static site can't pass WebSocket connections on,
    // which silently broke online status, live messages and live notifications.
    const base = process.env.REACT_APP_BACKEND_URL;
    if (base) {
      try {
        const u = new URL(base);
        return `${u.protocol === 'https:' ? 'wss' : 'ws'}://${u.host}/api/ws/${token}`;
      } catch { /* fall back to the page's host */ }
    }
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${wsProtocol}://${window.location.host}/api/ws/${token}`;
  };

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const [notifResponse, countResponse] = await Promise.all([
        notificationsAPI.getNotifications(50),
        notificationsAPI.getUnreadCount()
      ]);
      setNotifications(notifResponse.data);
      setUnreadCount(countResponse.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    const token = getToken();
    if (!token || !user) return;

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsUrl = getWsUrl();
    if (!wsUrl) return;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      window.__bridgeway_ws = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'init') {
            setUnreadCount(data.data.unread_count);
          } else if (data.type === 'notification') {
            // Add new notification to the top of the list
            setNotifications(prev => [data.data, ...prev]);
            setUnreadCount(prev => prev + 1);
            
            // Show toast notification
            showNotificationToast(data.data);
          } else if (data.type === 'new_message') {
            // Show toast for new message
            toast(
              <div className="flex items-start space-x-3">
                <span className="text-xl">💬</span>
                <div>
                  <p className="font-semibold text-sm">{data.data.sender_name}</p>
                  <p className="text-xs text-slate-600 mt-0.5">{data.data.content?.substring(0, 50)}</p>
                </div>
              </div>,
              { duration: 4000, position: 'top-right' }
            );
          }
        } catch (e) {
          // Handle ping/pong text messages
          if (event.data === 'ping') {
            ws.send('pong');
          }
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code);
        wsRef.current = null;
        window.__bridgeway_ws = null;
        
        // Reconnect after 5 seconds if not intentionally closed
        if (event.code !== 1000 && getToken()) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 5000);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }, [user]);

  // Show toast for new notification
  const showNotificationToast = (notification) => {
    const iconMap = {
      'booking_confirmed': '✅',
      'new_booking': '📅',
      'booking_cancelled': '❌',
      'new_review': '⭐',
      'tutor_approved': '🎉',
      'tutor_rejected': '📋',
      'new_tutor_application': '👤',
    };

    const icon = iconMap[notification.type] || '🔔';
    
    toast(
      <div className="flex items-start space-x-3">
        <span className="text-xl">{icon}</span>
        <div>
          <p className="font-semibold text-sm">{notification.title}</p>
          <p className="text-xs text-slate-600 mt-0.5">{notification.message}</p>
        </div>
      </div>,
      {
        duration: 5000,
        position: 'top-right',
      }
    );
  };

  // Mark single notification as read
  const markAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Delete a notification
  const deleteNotification = async (notificationId) => {
    try {
      const notification = notifications.find(n => n.id === notificationId);
      await notificationsAPI.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  // Clear all notifications
  const clearAll = async () => {
    try {
      await notificationsAPI.clearAll();
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  // Connect WebSocket when user logs in
  useEffect(() => {
    if (user) {
      fetchNotifications();
      connectWebSocket();
    } else {
      // Disconnect when user logs out
      if (wsRef.current) {
        wsRef.current.close(1000);
        wsRef.current = null;
      }
      setNotifications([]);
      setUnreadCount(0);
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000);
      }
    };
  }, [user, fetchNotifications, connectWebSocket]);

  const value = {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
